/**
 * YetkiKullanicilarSekmesi.jsx — Yetki Yönetimi › Kullanıcılar (backlog #309, 2026-10-10)
 *
 * Bütün kullanıcılar (personel + firma tarafı) tek listede, ayrıntılı süzgeçle:
 * ad / kullanıcı adı / e-posta / id, rol, personel mi firma mı, aktiflik, firma, firmasız, firmanın uyruğu,
 * firmanın bir fuardaki rolü (Katılımcı / Organizatör / Dekoratör / Taşeron) + fuar, hiç giriş yapmamış,
 * son giriş aralığı. Backend: GET /rbac/users (rbac.manage).
 *
 * Satır işlemleri:
 *  - Rol: başka role taşır. Personel (denetmen) rolüne almak ya da personel rolünden çıkarmak da
 *    buradan (PUT /rbac/users/{id}/group). Personel = tüm firmaların verisini görür; uyarı gösterilir.
 *  - Firma: kullanıcıyı bir firmaya bağlar (o firma TEK firması olur) ya da bağlantıyı kaldırır.
 *  - Geçmiş: o kullanıcının işlem geçmişini açar.
 * Aktif / pasif burada değiştirilmiyor: canlı giriş eşitlemesi (LoginService) user_active'i canlıdan
 * yazıyor, buradaki değişiklik bir sonraki girişte geri dönerdi. Kullanıcılar sayfası düzenliyor.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Card, CardBody, CardHeader, Row, Col, Button, Alert, Spinner, Input, Label, Table, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter, Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
import { Filter, RotateCcw, Building2, UserCog, History, X } from "lucide-react";
import FirmaSearchSelect from "../../../Components/Firma/FirmaSearchSelect";
import TarihGirdisi from "../../../Components/Common/TarihGirdisi";
import { listManagedUsers, setManagedUserGroup, setUserFirma, removeUserFirma } from "../../../services/rbacService";
import { fuarService } from "../../../services/fuarService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const PAGE_SIZE = 50;

const EMPTY_FILTERS = {
  q: "", groupId: "", staff: "", active: "", firma: null, firmaVar: "", firmaNation: "",
  firmaTypeId: "", fuarId: "", girisVar: "", sonGirisBaslangic: "", sonGirisBitis: "",
};

const FIRMA_ROLLERI = [
  { value: "1", get label() { return i18n.t("ayarlar:yetkiKullanicilarSekmesi.katilimci"); } },
  { value: "2", get label() { return i18n.t("ayarlar:yetkiKullanicilarSekmesi.organizator"); } },
  { value: "3", get label() { return i18n.t("ayarlar:yetkiKullanicilarSekmesi.dekorator"); } },
  { value: "4", get label() { return i18n.t("ayarlar:yetkiKullanicilarSekmesi.taseronTumu"); } },
];

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(currentLocaleTag());
};
const fullName = (u) => [u.userName, u.userSurname].filter(Boolean).join(" ") || u.userLoginName || `#${u.userId}`;

export function RolRozeti({ user }) {
  const { t } = useTranslation("ayarlar");
  if (user.root) return <Badge color="danger" pill>{t("yetkiKullanicilarSekmesi.kok")}</Badge>;
  if (user.staff) return <Badge color="success" pill>{t("yetkiKullanicilarSekmesi.personel")}</Badge>;
  if (user.userGroupId == null) return <Badge color="secondary" pill>{t("yetkiKullanicilarSekmesi.rolsuz")}</Badge>;
  return <Badge color="warning" pill>{t("yetkiKullanicilarSekmesi.firmaTarafi")}</Badge>;
}

export function FirmaHucresi({ firmalar }) {
  const { t } = useTranslation("ayarlar");
  if (!firmalar || firmalar.length === 0) return <span className="text-muted small">{t("yetkiKullanicilarSekmesi.firmaYok")}</span>;
  return (
    <div className="d-flex flex-column gap-1">
      {firmalar.map((f) => (
        <div key={f.firmaId} className="small">
          <span className="fw-semibold">{f.firmaName || t("yetkiKullanicilarSekmesi.firmaKaydiYok")}</span>{" "}
          <span className="text-muted font-monospace">#{f.firmaId}</span>
          {f.yabanci && <Badge color="info" className="ms-1">{t("yetkiKullanicilarSekmesi.yabanci")}</Badge>}
          {(f.fuarRolAdlari || []).length > 0 && (
            <div className="text-muted" style={{ fontSize: 11 }}>{f.fuarRolAdlari.join(", ")}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function YetkiKullanicilarSekmesi({ roles, me, onReload, onShowHistory }) {
  const { t: tx } = useTranslation("ayarlar");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [query, setQuery] = useState("");   // debounced q
  const [sort, setSort] = useState("ad");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fuarlar, setFuarlar] = useState([]);
  const [reloadToken, setReloadToken] = useState(0);
  const [rolModal, setRolModal] = useState(null);     // user
  const [firmaModal, setFirmaModal] = useState(null); // user

  const set = (key, value) => { setFilters((p) => ({ ...p, [key]: value })); setPage(0); };

  useEffect(() => {
    const t = setTimeout(() => { setQuery(filters.q.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const list = await fuarService.getSelectableFuarList();
        if (!ignore) setFuarlar(list || []);
      } catch {
        if (!ignore) setFuarlar([]);
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  const params = useMemo(() => ({
    q: query,
    groupId: filters.groupId,
    staff: filters.staff,
    active: filters.active,
    firmaId: filters.firma?.firmaId,
    firmaVar: filters.firmaVar,
    firmaNation: filters.firmaNation,
    firmaTypeId: filters.firmaTypeId,
    fuarId: filters.fuarId,
    girisVar: filters.girisVar,
    sonGirisBaslangic: filters.sonGirisBaslangic,
    sonGirisBitis: filters.sonGirisBitis,
    sort,
    page,
    size: PAGE_SIZE,
  }), [query, filters, sort, page]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const out = await listManagedUsers(params);
        if (!ignore) setData(out);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [params, reloadToken]);

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const reloadList = async () => { setReloadToken((n) => n + 1); await onReload?.(); };

  return (
    <>
      <Card>
        <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
          <Filter size={16} className="text-muted" />
          <h5 className="card-title mb-0 flex-grow-1">{tx("yetkiKullanicilarSekmesi.kullaniciSuzgeci")}</h5>
          <Button size="sm" color="light" className="border" onClick={() => { setFilters(EMPTY_FILTERS); setSort("ad"); setPage(0); }}>
            <RotateCcw size={14} className="me-1" />{tx("yetkiKullanicilarSekmesi.temizle")}
          </Button>
        </CardHeader>
        <CardBody>
          <Row className="g-2">
            <Col md={4}>
              <Label for="yk-q" className="form-label small mb-1">{tx("common:ara")}</Label>
              <Input id="yk-q" bsSize="sm" placeholder={tx("yetkiKullanicilarSekmesi.adKullaniciAdiEPosta")} value={filters.q} onChange={(e) => set("q", e.target.value)} />
            </Col>
            <Col md={3}>
              <Label for="yk-rol" className="form-label small mb-1">{tx("yetkiKullanicilarSekmesi.rol")}</Label>
              <Input id="yk-rol" type="select" bsSize="sm" value={filters.groupId} onChange={(e) => set("groupId", e.target.value)}>
                <option value="">{tx("yetkiKullanicilarSekmesi.hepsi")}</option>
                {roles.map((r) => <option key={r.userGroupId} value={r.userGroupId}>{r.userGroupName}</option>)}
              </Input>
            </Col>
            <Col md={2}>
              <Label for="yk-taraf" className="form-label small mb-1">{tx("yetkiKullanicilarSekmesi.taraf")}</Label>
              <Input id="yk-taraf" type="select" bsSize="sm" value={filters.staff} onChange={(e) => set("staff", e.target.value)}>
                <option value="">{tx("yetkiKullanicilarSekmesi.hepsi")}</option>
                <option value="true">{tx("yetkiKullanicilarSekmesi.personelDenetmen")}</option>
                <option value="false">{tx("yetkiKullanicilarSekmesi.firmaTarafi")}</option>
              </Input>
            </Col>
            <Col md={1}>
              <Label for="yk-aktif" className="form-label small mb-1">{tx("yetkiKullanicilarSekmesi.durum")}</Label>
              <Input id="yk-aktif" type="select" bsSize="sm" value={filters.active} onChange={(e) => set("active", e.target.value)}>
                <option value="">{tx("yetkiKullanicilarSekmesi.hepsi")}</option>
                <option value="1">{tx("yetkiKullanicilarSekmesi.aktif")}</option>
                <option value="0">{tx("yetkiKullanicilarSekmesi.pasif")}</option>
              </Input>
            </Col>
            <Col md={2}>
              <Label for="yk-sirala" className="form-label small mb-1">{tx("yetkiKullanicilarSekmesi.sirala")}</Label>
              <Input id="yk-sirala" type="select" bsSize="sm" value={sort} onChange={(e) => { setSort(e.target.value); setPage(0); }}>
                <option value="ad">{tx("yetkiKullanicilarSekmesi.adaGore")}</option>
                <option value="sonGiris">{tx("yetkiKullanicilarSekmesi.sonGiriseGore")}</option>
                <option value="kayit">{tx("yetkiKullanicilarSekmesi.kayitTarihineGore")}</option>
                <option value="rol">{tx("yetkiKullanicilarSekmesi.roleGore")}</option>
              </Input>
            </Col>
            <Col md={4}>
              <FirmaSearchSelect
                forceLive bsSize="sm" label={tx("yetkiKullanicilarSekmesi.firma")} value={filters.firma}
                onChange={(f) => set("firma", f)}
              />
            </Col>
            <Col md={2}>
              <Label for="yk-firmavar" className="form-label small mb-1">{tx("yetkiKullanicilarSekmesi.firmaBaglantisi")}</Label>
              <Input id="yk-firmavar" type="select" bsSize="sm" value={filters.firmaVar} onChange={(e) => set("firmaVar", e.target.value)}>
                <option value="">{tx("yetkiKullanicilarSekmesi.hepsi")}</option>
                <option value="true">{tx("yetkiKullanicilarSekmesi.firmasiVar")}</option>
                <option value="false">{tx("yetkiKullanicilarSekmesi.firmasiYok")}</option>
              </Input>
            </Col>
            <Col md={2}>
              <Label for="yk-uyruk" className="form-label small mb-1">{tx("yetkiKullanicilarSekmesi.firmaUyrugu")}</Label>
              <Input id="yk-uyruk" type="select" bsSize="sm" value={filters.firmaNation} onChange={(e) => set("firmaNation", e.target.value)}>
                <option value="">{tx("yetkiKullanicilarSekmesi.hepsi")}</option>
                <option value="1">{tx("yetkiKullanicilarSekmesi.yerli")}</option>
                <option value="2">{tx("yetkiKullanicilarSekmesi.yabanci")}</option>
              </Input>
            </Col>
            <Col md={2}>
              <Label for="yk-firmarol" className="form-label small mb-1">{tx("yetkiKullanicilarSekmesi.firmaninFuarRolu")}</Label>
              <Input id="yk-firmarol" type="select" bsSize="sm" value={filters.firmaTypeId} onChange={(e) => set("firmaTypeId", e.target.value)}>
                <option value="">{tx("yetkiKullanicilarSekmesi.hepsi")}</option>
                {FIRMA_ROLLERI.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </Input>
            </Col>
            <Col md={2}>
              <Label for="yk-fuar" className="form-label small mb-1">{tx("yetkiKullanicilarSekmesi.fuar")}</Label>
              <Input id="yk-fuar" type="select" bsSize="sm" value={filters.fuarId} onChange={(e) => set("fuarId", e.target.value)}>
                <option value="">{tx("yetkiKullanicilarSekmesi.hepsi")}</option>
                {fuarlar.map((f) => <option key={f.fuarId} value={f.fuarId}>{f.fuarName || f.fuarShortName || `#${f.fuarId}`}</option>)}
              </Input>
            </Col>
            <Col md={2}>
              <Label for="yk-giris" className="form-label small mb-1">{tx("yetkiKullanicilarSekmesi.giris")}</Label>
              <Input id="yk-giris" type="select" bsSize="sm" value={filters.girisVar} onChange={(e) => set("girisVar", e.target.value)}>
                <option value="">{tx("yetkiKullanicilarSekmesi.hepsi")}</option>
                <option value="true">{tx("yetkiKullanicilarSekmesi.girisYapmis")}</option>
                <option value="false">{tx("yetkiKullanicilarSekmesi.hicGirisYapmamis")}</option>
              </Input>
            </Col>
            <Col md={2}>
              <Label for="yk-giris-bas" className="form-label small mb-1">{tx("yetkiKullanicilarSekmesi.sonGirisBaslangic")}</Label>
              <TarihGirdisi id="yk-giris-bas" value={filters.sonGirisBaslangic} onChange={(v) => set("sonGirisBaslangic", v)} />
            </Col>
            <Col md={2}>
              <Label for="yk-giris-bit" className="form-label small mb-1">{tx("yetkiKullanicilarSekmesi.sonGirisBitis")}</Label>
              <TarihGirdisi id="yk-giris-bit" value={filters.sonGirisBitis} onChange={(v) => set("sonGirisBitis", v)} />
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="d-flex align-items-center gap-2">
          <h5 className="card-title mb-0 flex-grow-1">{tx("yetkiKullanicilarSekmesi.kullanicilar")}</h5>
          <span className="text-muted small">{loading ? tx("yetkiKullanicilarSekmesi.yukleniyor") : tx("yetkiKullanicilarSekmesi.kullanici", { total })}</span>
        </CardHeader>
        <CardBody className="p-0">
          {error && <Alert color="danger" className="m-3 mb-0" toggle={() => setError(null)}>{error}</Alert>}
          <div className="table-responsive">
            <Table className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-3">{tx("yetkiKullanicilarSekmesi.kullanici2")}</th>
                  <th>{tx("yetkiKullanicilarSekmesi.rol")}</th>
                  <th>{tx("yetkiKullanicilarSekmesi.firma")}</th>
                  <th>{tx("yetkiKullanicilarSekmesi.sonGiris")}</th>
                  <th className="text-center">{tx("yetkiKullanicilarSekmesi.durum")}</th>
                  <th className="text-end pe-3" style={{ width: 230 }}>{tx("yetkiKullanicilarSekmesi.islemler")}</th>
                </tr>
              </thead>
              <tbody>
                {loading && !data && (
                  <tr><td colSpan={6} className="text-center py-4"><Spinner color="primary" size="sm" /></td></tr>
                )}
                {data && data.users.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-4">{tx("yetkiKullanicilarSekmesi.buSuzgeceUyanKullaniciYok")}</td></tr>
                )}
                {(data?.users ?? []).map((u) => (
                  <tr key={u.userId}>
                    <td className="ps-3">
                      <div className="fw-semibold">{fullName(u)}</div>
                      <div className="small text-muted">
                        <span className="font-monospace">{u.userLoginName}</span> · #{u.userId}
                        {u.userEmail ? ` · ${u.userEmail}` : ""}
                      </div>
                    </td>
                    <td>
                      <div className="small fw-semibold">{u.userGroupName || "—"}</div>
                      <RolRozeti user={u} />
                    </td>
                    <td><FirmaHucresi firmalar={u.firmalar} /></td>
                    <td className="small text-muted">{fmtDate(u.userLastLogin)}</td>
                    <td className="text-center">
                      <Badge color={u.userActive === 1 ? "success" : "secondary"} pill>{u.userActive === 1 ? tx("yetkiKullanicilarSekmesi.aktif") : tx("yetkiKullanicilarSekmesi.pasif")}</Badge>
                    </td>
                    <td className="text-end pe-3">
                      <div className="d-flex gap-1 justify-content-end">
                        <Button size="sm" color="primary" outline disabled={u.root} title={u.root ? tx("yetkiKullanicilarSekmesi.kokYalnizcaKokDevriIle") : tx("yetkiKullanicilarSekmesi.roluDegistir")} onClick={() => setRolModal(u)}>
                          <UserCog size={14} className="me-1" />{tx("yetkiKullanicilarSekmesi.rol")}
                        </Button>
                        <Button size="sm" color="secondary" outline title={tx("yetkiKullanicilarSekmesi.firmaBaglantisi")} onClick={() => setFirmaModal(u)}>
                          <Building2 size={14} className="me-1" />{tx("yetkiKullanicilarSekmesi.firma")}
                        </Button>
                        <Button size="sm" color="light" className="border" title={tx("yetkiKullanicilarSekmesi.islemGecmisi")} onClick={() => onShowHistory?.({ hedefTipi: "KULLANICI", hedefId: u.userId, ad: fullName(u) })}>
                          <History size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {total > PAGE_SIZE && (
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
              <span className="text-muted small">{tx("yetkiKullanicilarSekmesi.sayfa", { v: page + 1 })}{" "}{pageCount}</span>
              <Pagination size="sm" className="mb-0">
                <PaginationItem disabled={page === 0}>
                  <PaginationLink previous onClick={() => setPage((p) => Math.max(0, p - 1))} />
                </PaginationItem>
                <PaginationItem disabled={page + 1 >= pageCount}>
                  <PaginationLink next onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} />
                </PaginationItem>
              </Pagination>
            </div>
          )}
        </CardBody>
      </Card>

      {rolModal && (
        <RolDegistirModal user={rolModal} roles={roles} me={me} onClose={() => setRolModal(null)}
          onSaved={async () => { setRolModal(null); await reloadList(); }} />
      )}
      {firmaModal && (
        <FirmaBaglaModal user={firmaModal} me={me} onClose={() => setFirmaModal(null)}
          onSaved={async () => { setFirmaModal(null); await reloadList(); }} />
      )}
    </>
  );
}

/** Rol değiştirme. Personel sınırını geçen değişiklikte ne olacağını açıkça söyler. */
function RolDegistirModal({ user, roles, me, onClose, onSaved }) {
  const { t } = useTranslation("ayarlar");
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const options = roles.filter((r) => !r.root && r.userGroupId !== user.userGroupId);
  const targetRole = roles.find((r) => String(r.userGroupId) === String(target));
  const ownRole = !me?.root && me?.userId === user.userId;
  const becomesStaff = targetRole && targetRole.staff && !user.staff;
  const leavesStaff = targetRole && !targetRole.staff && user.staff;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await setManagedUserGroup(user.userId, Number(target));
      await onSaved();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  return (
    <Modal isOpen toggle={() => !busy && onClose()} centered>
      <ModalHeader toggle={() => !busy && onClose()}>{t("yetkiKullanicilarSekmesi.roluDegistir2")}{" "}{fullName(user)}</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {ownRole && <Alert color="warning" className="small">{t("yetkiKullanicilarSekmesi.kendiRolunuzuDegistiremezsinizBunuKok")}</Alert>}
        <p className="mb-2 small">{t("yetkiKullanicilarSekmesi.suAnkiRol")}{" "}<span className="fw-semibold">{user.userGroupName || "—"}</span> <RolRozeti user={user} /></p>
        <Label for="rol-hedef" className="form-label">{t("yetkiKullanicilarSekmesi.yeniRol")}</Label>
        <Input id="rol-hedef" type="select" value={target} disabled={busy || ownRole} onChange={(e) => setTarget(e.target.value)}>
          <option value="">{t("yetkiKullanicilarSekmesi.rolSecin")}</option>
          <optgroup label={t("yetkiKullanicilarSekmesi.personelDenetmenRolleri")}>
            {options.filter((r) => r.staff).map((r) => <option key={r.userGroupId} value={r.userGroupId}>{r.userGroupName}</option>)}
          </optgroup>
          <optgroup label={t("yetkiKullanicilarSekmesi.firmaTarafiRoller")}>
            {options.filter((r) => !r.staff).map((r) => <option key={r.userGroupId} value={r.userGroupId}>{r.userGroupName}</option>)}
          </optgroup>
        </Input>
        {becomesStaff && (
          <Alert color="danger" className="small mt-3 mb-0">
            {t("yetkiKullanicilarSekmesi.buKullanici")}{" "}<b>{t("yetkiKullanicilarSekmesi.personel2")}</b>{" "}{t("yetkiKullanicilarSekmesi.olacakFirmaPortalindanCikarSaka")}{" "}<b>{t("yetkiKullanicilarSekmesi.tumFirmalarin")}</b>{" "}{t("yetkiKullanicilarSekmesi.verisiniKisiBelgeleriDahilGorur")}
          </Alert>
        )}
        {leavesStaff && (
          <Alert color="warning" className="small mt-3 mb-0">
            {t("yetkiKullanicilarSekmesi.buKullanicininPersonelYetkileriKalkacak")}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={busy} onClick={onClose}>{t("yetkiKullanicilarSekmesi.vazgec")}</Button>
        <Button color={becomesStaff ? "danger" : "primary"} disabled={busy || !target || ownRole} onClick={submit}>
          {busy ? <Spinner size="sm" /> : t("yetkiKullanicilarSekmesi.roluDegistir")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/** Firma bağlantısı: yeni firmaya bağla (TEK firması olur) ya da mevcut bağlantıyı kaldır. */
function FirmaBaglaModal({ user, me, onClose, onSaved }) {
  const { t } = useTranslation("ayarlar");
  const [firma, setFirma] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const own = !me?.root && me?.userId === user.userId;
  const current = user.firmalar || [];

  async function link() {
    setBusy(true);
    setError(null);
    try {
      await setUserFirma(user.userId, firma.firmaId);
      await onSaved();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  async function unlink(firmaId) {
    setBusy(true);
    setError(null);
    try {
      await removeUserFirma(user.userId, firmaId);
      await onSaved();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  return (
    <Modal isOpen toggle={() => !busy && onClose()} centered size="lg">
      <ModalHeader toggle={() => !busy && onClose()}>{t("yetkiKullanicilarSekmesi.firmaBaglantisi2")}{" "}{fullName(user)}</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {own && <Alert color="warning" className="small">{t("yetkiKullanicilarSekmesi.kendiFirmaBaglantiniziDegistiremezsinizBunu")}</Alert>}
        <h6 className="mb-2">{t("yetkiKullanicilarSekmesi.bagliOlduguFirma")}</h6>
        {current.length === 0 ? (
          <p className="text-muted small">{t("yetkiKullanicilarSekmesi.buKullaniciHicbirFirmayaBagli")}{!user.staff && t("yetkiKullanicilarSekmesi.firmaTarafindakiBirKullaniciFirmasiz")}</p>
        ) : (
          <Table size="sm" className="mb-3">
            <tbody>
              {current.map((f) => (
                <tr key={f.firmaId}>
                  <td>
                    <span className="fw-semibold">{f.firmaName || t("yetkiKullanicilarSekmesi.firmaKaydiYok")}</span>{" "}
                    <span className="text-muted font-monospace small">#{f.firmaId}</span>
                    {f.yabanci && <Badge color="info" className="ms-1">{t("yetkiKullanicilarSekmesi.yabanci")}</Badge>}
                  </td>
                  <td className="text-end">
                    <Button size="sm" color="danger" outline disabled={busy || own} onClick={() => unlink(f.firmaId)}>
                      <X size={13} className="me-1" />{t("yetkiKullanicilarSekmesi.baglantiyiKaldir")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <h6 className="mb-2">{t("yetkiKullanicilarSekmesi.baskaBirFirmayaBagla")}</h6>
        <FirmaSearchSelect forceLive value={firma} onChange={setFirma} disabled={busy || own} />
        {firma && current.length > 0 && (
          <Alert color="warning" className="small mt-2 mb-0">
            {t("yetkiKullanicilarSekmesi.secilenFirmaKullanicininTekFirmasi")}{current.length > 1 ? t("yetkiKullanicilarSekmesi.lar") : ""}{" "}{t("yetkiKullanicilarSekmesi.kaldirilirFirmaKullanicisiBundanSonra")}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={busy} onClick={onClose}>{t("common:kapat")}</Button>
        <Button color="primary" disabled={busy || !firma || own} onClick={link}>
          {busy ? <Spinner size="sm" /> : t("yetkiKullanicilarSekmesi.buFirmayaBagla")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
